// ─── Workshop Truck Availability Tracker ───
// Helper utilities, constants & formatting functions
// IASL Technical Academy / Lafarge Driving Institute, Nigeria

import type { WorkshopStatus } from '@/types'

// ─── Status Colour Maps ────────────────────────────────────

/** Hex colour per workshop status – used in Recharts / charts */
export const STATUS_COLORS: Record<WorkshopStatus, string> = {
  'Active Fleet': '#10B981',
  'In Assessment': '#F59E0B',
  'Awaiting Parts': '#EF4444',
  'Work In Progress': '#8B5CF6',
  'Ready for Release': '#06B6D4',
  'Released': '#6B7280',
}

/** Tailwind utility classes per workshop status – used in badges */
export const STATUS_BG_COLORS: Record<WorkshopStatus, string> = {
  'Active Fleet': 'bg-green-100 text-green-800',
  'In Assessment': 'bg-amber-100 text-amber-800',
  'Awaiting Parts': 'bg-red-100 text-red-800',
  'Work In Progress': 'bg-purple-100 text-purple-800',
  'Ready for Release': 'bg-teal-100 text-teal-800',
  'Released': 'bg-gray-100 text-gray-700',
}

/** Ordered list of all workshop statuses */
export const ALL_STATUSES: WorkshopStatus[] = [
  'Active Fleet',
  'In Assessment',
  'Awaiting Parts',
  'Work In Progress',
  'Ready for Release',
  'Released',
]

// ─── Date / Duration Helpers ────────────────────────────────

/**
 * Calculate the number of whole days a truck has been in the workshop.
 * Returns `null` when no entry date is provided.
 */
export function calcDaysInWorkshop(entryDate: string | null): number | null {
  if (!entryDate) return null
  const entry = new Date(entryDate)
  const today = new Date()
  const diff = Math.floor(
    (today.getTime() - entry.getTime()) / (1000 * 60 * 60 * 24)
  )
  return diff >= 0 ? diff : 0
}

/**
 * Return a Tailwind text‑colour class based on days in workshop.
 * ≤ 3 days → green, ≤ 7 → amber, > 7 → red.
 */
export function getDaysColor(days: number | null): string {
  if (days === null) return 'text-gray-400'
  if (days <= 3) return 'text-green-600 font-semibold'
  if (days <= 7) return 'text-amber-600 font-semibold'
  return 'text-red-600 font-bold'
}

/**
 * Return a Tailwind text‑colour class based on availability percentage.
 * ≥ 90 % → green, ≥ 70 % → amber, < 70 % → red.
 */
export function getAvailabilityColor(pct: number): string {
  if (pct >= 90) return 'text-green-600'
  if (pct >= 70) return 'text-amber-600'
  return 'text-red-600'
}

// ─── Formatting Helpers ─────────────────────────────────────

/** Format an ISO date string to "dd MMM yyyy" (South‑African locale). */
export function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return '—'
  }
}

/** Format an ISO date string to "dd MMM yyyy, HH:mm" (South‑African locale). */
export function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '—'
  }
}

// ─── Make Normalisation ──────────────────────────────────────

/**
 * Canonical brand map.
 * Keys are lowercased; values are the display name.
 * We also strip anything after the first space/hyphen that looks like a
 * model code (e.g. "SCANIA P410 - XT" → key "scania").
 */
const MAKE_ALIASES: Record<string, string> = {
  // SinoTruk variants
  'howo': 'SinoTruk',
  'sinotruk': 'SinoTruk',
  'sino truk': 'SinoTruk',
  'sino-truk': 'SinoTruk',

  // Mercedes-Benz variants
  'm.benz': 'Mercedes Benz',
  'm benz': 'Mercedes Benz',
  'mbenz': 'Mercedes Benz',
  'mercedes': 'Mercedes Benz',
  'mercedes benz': 'Mercedes Benz',
  'mercedes-benz': 'Mercedes Benz',
  'mercedesbenz': 'Mercedes Benz',

  // Scania (canonical casing)
  'scania': 'Scania',

  // Other makes
  'ford': 'Ford',
  'toyota': 'Toyota',
  'man': 'MAN',
  'daf': 'DAF',
  'volvo': 'Volvo',
  'iveco': 'Iveco',
}

/** Normalise a raw vehicle make string to a canonical display name. */
export function normalizeMake(make: string | null | undefined): string {
  if (!make) return 'Unknown'
  const trimmed = make.trim()

  // Direct full-string lookup (case-insensitive)
  const directKey = trimmed.toLowerCase()
  if (MAKE_ALIASES[directKey]) return MAKE_ALIASES[directKey]

  // Try matching only the first word — handles "SCANIA P410 - XT", "Sinotruk 6x4", etc.
  const firstWord = trimmed.split(/[\s\-,]/)[0].toLowerCase()
  if (MAKE_ALIASES[firstWord]) return MAKE_ALIASES[firstWord]

  // Return with title-case normalisation as a fallback
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
}

/** Truncate a string to `maxLen` characters, appending "…" if needed. */
export function truncate(str: string, maxLen: number): string {
  if (!str) return '—'
  return str.length > maxLen ? str.slice(0, maxLen) + '...' : str
}
